import { apiErrorMessage } from '../utils/apiErrorMessage';
import { postJsonWithChallenge } from '../utils/challengeFetch';
import { retryAfterSeconds } from '../utils/otpGuardResponse';

const API_URL = import.meta.env.VITE_API_URL;

export interface PasswordResetResponse {
  success: boolean;
  message: string;
  email: string;
  next_step?: string;
  expires_in_minutes?: number;
  action?: string;
  reset_token_valid?: boolean;
}

export interface PasswordResetError {
  error: string;
  action?: string;
  required_fields?: string[];
  hint?: string;
}

/** A refused reset step: `message` is the backend's own sentence; `retryAfter` the wait it asked for. */
export class PasswordResetRequestError extends Error {
  retryAfter?: number;
  constructor(message: string, retryAfter?: number) {
    super(message);
    this.name = 'PasswordResetRequestError';
    this.retryAfter = retryAfter;
  }
}

const postOrThrow = async (
  path: string,
  body: Record<string, unknown>,
  fallback: string,
): Promise<PasswordResetResponse> => {
  const response = await postJsonWithChallenge(`${API_URL}${path}`, body);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new PasswordResetRequestError(apiErrorMessage(data, fallback), retryAfterSeconds(data, response.headers));
  }
  return data as PasswordResetResponse;
};

export const requestPasswordReset = (email: string): Promise<PasswordResetResponse> =>
  postOrThrow('/forgot-password', { email }, 'Failed to request password reset');

export const verifyResetOTP = (email: string, otpCode: string): Promise<PasswordResetResponse> =>
  postOrThrow('/verify-reset-otp', { email, otp_code: otpCode }, 'Failed to verify OTP');

export const resetPassword = (
  email: string,
  otpCode: string,
  newPassword: string,
  confirmPassword: string,
): Promise<PasswordResetResponse> =>
  postOrThrow(
    '/reset-password',
    { email, otp_code: otpCode, new_password: newPassword, confirm_password: confirmPassword },
    'Failed to reset password',
  );
